import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import TopBar from '../../../layout/TopBar';
import LoadingSpinner from '../../../components/shared/LoadingSpinner';

// Interface temporaire pour la structure des données
interface JurisprudenceData {
    id: string;
    title: string;
    court: string;
    date: string;
    number: string;
    summary: string;
    content: string;
    keywords: string[];
    references: string[];
}

const JurisprudenceDetail = () => {
    const { id } = useParams<{ id: string }>();

    const [jurisprudence, setJurisprudence] = useState<JurisprudenceData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Simulation d'un appel API - à remplacer par la vraie API
        const fetchJurisprudence = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Simulation d'un délai d'API
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Données mockées - à remplacer par l'appel API réel
                const mockData: JurisprudenceData = {
                    id: id || '',
                    title: `Jurisprudence ${id}`,
                    court: 'Cour de cassation, chambre civile 1',
                    date: '15 mars 2024',
                    number: `24-${id}`,
                    summary: 'Résumé de la jurisprudence concernant les aspects juridiques importants de cette affaire...',
                    content: `Contenu complet de la jurisprudence ${id}. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.

Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.

Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.`,
                    keywords: ['Droit civil', 'Responsabilité', 'Contrat', 'Dommages-intérêts'],
                    references: ['Art. 1240 du Code civil', 'Art. 1241 du Code civil', 'Cass. 1re civ., 12 juil. 2023']
                };

                setJurisprudence(mockData);
            } catch (err) {
                setError('Erreur lors du chargement de la jurisprudence');
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchJurisprudence();
        } else {
            setError('ID de jurisprudence manquant');
            setIsLoading(false);
        }
    }, [id]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500">
                <TopBar />
                <div className="pt-24 flex justify-center items-center">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (error || !jurisprudence) {
        return (
            <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500">
                <TopBar />
                <div className="pt-24 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center py-12">
                            <h1 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
                                Jurisprudence introuvable
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                {error || 'Cette jurisprudence n\'existe pas ou n\'est plus disponible.'}
                            </p>
                            <Link
                                to="/jurisprudences"
                                className="inline-flex items-center px-4 py-2 bg-mcdm-third-300 hover:bg-mcdm-third-400 text-white rounded-lg transition-colors duration-200"
                            >
                                ← Retour aux jurisprudences
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500 pb-10">
            <TopBar />
            <div className="pt-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">

                    {/* Breadcrumb */}
                    <nav className="mb-6">
                        <ol className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <li>
                                <Link
                                    to="/jurisprudences"
                                    className="hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-100 transition-colors duration-200"
                                >
                                    Jurisprudences
                                </Link>
                            </li>
                            <li className="flex items-center">
                                <span className="mx-2">/</span>
                                <span className="text-mcdm-third-500 dark:text-mcdm-primary-100">
                                    {jurisprudence.number}
                                </span>
                            </li>
                        </ol>
                    </nav>

                    {/* En-tête de la jurisprudence */}
                    <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6 mb-6">
                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-4">
                            <div className="mb-4 lg:mb-0">
                                <h1 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                    {jurisprudence.title}
                                </h1>
                                <p className="text-lg text-gray-700 dark:text-gray-300 mb-1">
                                    {jurisprudence.court}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {jurisprudence.date} • {jurisprudence.number}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex space-x-3">
                                <button className="px-4 py-2 bg-mcdm-third-300 hover:bg-mcdm-third-400 text-white rounded-lg transition-colors duration-200 text-sm">
                                    Suivre
                                </button>
                                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 text-sm">
                                    Partager
                                </button>
                            </div>
                        </div>

                        {/* Mots-clés */}
                        <div className="flex flex-wrap gap-2">
                            {jurisprudence.keywords.map((keyword, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 bg-mcdm-primary-100 dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-white text-sm rounded-full"
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Colonne principale - Contenu */}
                        <div className="lg:col-span-2">

                            {/* Résumé */}
                            <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6 mb-6">
                                <h2 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                                    Résumé
                                </h2>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {jurisprudence.summary}
                                </p>
                            </div>

                            {/* Contenu complet */}
                            <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                                <h2 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
                                    Texte intégral
                                </h2>
                                <div className="prose dark:prose-invert max-w-none">
                                    {jurisprudence.content.split('\n\n').map((paragraph, index) => (
                                        <p key={index} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar - Informations complémentaires */}
                        <div className="space-y-6">

                            {/* Références juridiques */}
                            <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                                <h3 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
                                    Références juridiques
                                </h3>
                                <ul className="space-y-2">
                                    {jurisprudence.references.map((reference, index) => (
                                        <li key={index} className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                            {reference}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Jurisprudences similaires - Placeholder */}
                            <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                                <h3 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
                                    Jurisprudences similaires
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Fonctionnalité à implémenter...
                                </p>
                            </div>

                            {/* Actions rapides */}
                            <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                                <h3 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
                                    Actions
                                </h3>
                                <div className="space-y-3">
                                    <button className="w-full px-4 py-2 bg-mcdm-primary-100 hover:bg-mcdm-primary-200 text-mcdm-third-500 rounded-lg transition-colors duration-200 text-sm">
                                        Ajouter à un dossier
                                    </button>
                                    <button className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 text-sm">
                                        Télécharger PDF
                                    </button>
                                    <button className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 text-sm">
                                        Imprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JurisprudenceDetail;